// v1 client router. The shell (OWNER / CTV / DEALER / KHO / PENDING) comes from /api/v1/me and is a presentation hint only;
// every API re-checks role, ownership and consent on the server.
import { api, banner, errorBanner, h, isNetworkError, NETWORK_MESSAGE, rememberReturn, setDataMode, type Json } from "./ui.ts";
import { codeView, loginView, ownerSetupView, recoverView, resumeView, signupView } from "./auth.ts";
import { privateDataView, profileView, waitView } from "./account.ts";
import { ownerHome, ownerPending, ownerUserDetailView, ownerUsersView } from "./owner.ts";
import { todayView } from "./zones.ts";
import { customersView, leadDetailView, leadFormView, replyView } from "./leads.ts";
import { loc3View } from "./loc3.ts";
import { postComposerView } from "./post.ts";
import { ownerLibraryView } from "./library.ts";
import { ownerStopView } from "./ownerAi.ts";
import { myHoldsView, searchView } from "./stock.ts";
import { khoCheckView, khoQueueView } from "./kho.ts";
import { ownerHoldsView, ownerImportView, ownerPatternsView } from "./ownerStock.ts";
import { ownerDealsView } from "./ownerDeals.ts";
import { caseMemberView, myCasesView, ownerCaseView, ownerSupportView } from "./support.ts";
import { dealerLedgerView, walletView } from "./money.ts";
import { inboxView, openFromPushView, phoneSettingsView, refreshBell, startBell, syncPushOnStart } from "./notify.ts";
import { handoffDetailView, handoffQueueView, helpLearnView, memberContentView, onboardingIntroView, ownerAuditView, ownerBackupView, ownerContentView } from "./closure.ts";
import { ownerCommissionsView, ownerDealerDetailView, ownerDealersView, ownerExpensesView, ownerPolicyView, ownerReconView } from "./ownerMoney.ts";
import { adjustmentsView, dealerStatementView, exportsView, ordersBookView, reconView, sourcesView } from "./books.ts";
import { dealsListView, dealView, holdPageView } from "./deals.ts";
import { practiceView } from "./practice.ts";
import { dealerRangeView, khoLookupView } from "./bsv2.ts";

const root = document.getElementById("app")!;
const PUBLIC = new Set(["/dang-nhap", "/dang-ky", "/dang-ky/ma", "/dang-ky/tiep-tuc", "/khoi-phuc", "/owner-lan-dau"]);

async function publicRoute(p: string, message?: string): Promise<HTMLElement> {
  if (p === "/dang-ky") return signupView();
  if (p === "/dang-ky/ma") return codeView();
  if (p === "/dang-ky/tiep-tuc") return resumeView();
  if (p === "/khoi-phuc") return recoverView();
  if (p === "/owner-lan-dau") return ownerSetupView();
  if (p !== "/dang-nhap") history.replaceState(null, "", "/dang-nhap");
  return loginView(message);
}

/** Sổ sách pages (same screens for the Owner under /owner/tien/so-sach and the accountant under /ke-toan). */
function bookRoute(me: Json, rest: string): Promise<HTMLElement> | null {
  if (rest === "" || rest === "/") return ordersBookView(me, location.search);
  if (rest === "/nguon") return sourcesView(me);
  if (rest === "/doi-chieu") return reconView(me, location.search);
  if (rest === "/dieu-chinh") return adjustmentsView(me);
  if (rest === "/dai-ly") return dealerStatementView(me, location.search);
  if (rest === "/xuat-file") return exportsView(me, location.search);
  if (rest === "/doi-soat-dai-ly") return dealerRangeView(me, location.search);
  return null;
}

async function route(): Promise<HTMLElement> {
  const p = location.pathname.replace(/\/+$/, "") || "/";
  if (PUBLIC.has(p)) return publicRoute(p);
  let me: Json;
  try { me = await api("GET", "/api/v1/me"); }
  catch (e) {
    const err = e as { status?: number; message?: string };
    // UXAZ-03: a link opened without a session comes back to the same screen after sign-in (internal path only; the server re-checks it)
    if (err.status === 401 || err.status === 403) rememberReturn(`${p}${location.search}`);
    if (err.status === 401) return publicRoute("/dang-nhap");
    if (err.status === 403) return publicRoute("/dang-nhap", err.message);
    throw e;
  }
  setDataMode(me.data_mode);
  let m: RegExpMatchArray | null;
  // Owner slip 23/10 part A: bell count on every screen; once per page load, re-send this device's push registration if it is allowed
  void refreshBell();
  if (!pushSynced) { pushSynced = true; void syncPushOnStart(); }
  if (p === "/du-lieu-rieng") return privateDataView(me);
  if (p === "/thong-bao" || p === "/owner/thong-bao") return inboxView(me);
  if (p === "/thong-bao/mo") return openFromPushView(me);
  if (p === "/thong-bao/dien-thoai") return phoneSettingsView(me);
  if ((m = p.match(/^\/ho-so(?:\/([123]))?$/))) return profileView(m[1]);
  if (me.shell === "PENDING") {
    if (p === "/cho-duyet" || !me.submission || me.submission.status !== "SUBMITTED") {
      if (p === "/cho-duyet" || me.submission) return waitView(me);
      history.replaceState(null, "", "/ho-so"); return profileView();
    }
    history.replaceState(null, "", "/cho-duyet"); return waitView(me);
  }
  if (me.shell === "OWNER") {
    if (p === "/" || p === "/owner") { history.replaceState(null, "", "/owner"); return ownerHome(me); }
    if (p === "/owner/nguoi-dung") return ownerUsersView();
    if ((m = p.match(/^\/owner\/nguoi-dung\/([\w-]+)$/))) return ownerUserDetailView(m[1]);
    if (p === "/owner/kho") return ownerImportView();
    if (p === "/owner/kho/giu-so") return ownerHoldsView();
    if (p === "/owner/kho/kieu-so") return ownerPatternsView();
    if (p === "/owner/kho/giao-dich") return ownerDealsView();
    if (p === "/owner/kho/tra-sim") return khoLookupView(me);
    if (p === "/kho") return khoQueueView(me);
    if ((m = p.match(/^\/kho\/([\w-]+)$/))) return khoCheckView(m[1]);
    if (p === "/owner/ho-tro") return ownerSupportView();
    if ((m = p.match(/^\/owner\/ho-tro\/([\w-]+)$/))) return ownerCaseView(m[1]);
    if (p === "/owner/tien") return ownerCommissionsView();
    if (p === "/owner/tien/dai-ly") return ownerDealersView();
    if ((m = p.match(/^\/owner\/tien\/dai-ly\/([\w-]+)$/))) return ownerDealerDetailView(m[1]);
    if (p === "/owner/tien/doi-soat") return ownerReconView();
    if (p === "/owner/tien/chi-phi") return ownerExpensesView();
    if (p.startsWith("/owner/tien/so-sach")) { const v = bookRoute(me, p.slice("/owner/tien/so-sach".length)); if (v) return v; }
    if (p === "/owner/noi-dung") return ownerPolicyView();
    if (p === "/owner/noi-dung/chuan") return ownerContentView();
    if (p === "/owner/noi-dung/thu-vien") return ownerLibraryView();
    if (p === "/owner/nhat-ky/sao-luu") return ownerBackupView();
    if (p === "/owner/ca-chuyen") return handoffQueueView(me);
    if (p === "/luyen-tap") return practiceView(me);
    if (p === "/chot") return dealsListView(me);
    if ((m = p.match(/^\/chot\/([\w-]+)$/))) return dealView(me, m[1]);
    if ((m = p.match(/^\/owner\/ca-chuyen\/([\w-]+)$/))) return handoffDetailView(me, m[1]);
    if (p === "/owner/nhat-ky") return ownerAuditView();
    if (p === "/owner/dung") return ownerStopView();
    history.replaceState(null, "", "/owner"); return ownerHome(me);
  }
  if (me.shell === "ACCOUNTANT") {
    // bookkeeping console only (the server refuses every other surface for this role)
    if (p.startsWith("/ke-toan")) { const v = bookRoute(me, p.slice("/ke-toan".length)); if (v) return v; }
    if (p === "/luyen-tap") return practiceView(me);
    history.replaceState(null, "", "/ke-toan");
    return ordersBookView(me, "");
  }
  if (me.shell === "KHO") {
    if (p === "/kho/ca") return handoffQueueView(me);
    if (p === "/luyen-tap") return practiceView(me);
    if (p === "/chot") return dealsListView(me);
    if ((m = p.match(/^\/chot\/([\w-]+)$/))) return dealView(me, m[1]);
    if ((m = p.match(/^\/kho\/ca\/([\w-]+)$/))) return handoffDetailView(me, m[1]);
    if (p === "/noi-dung") return memberContentView();
    if (p === "/kho/tra-sim") return khoLookupView(me);
    if ((m = p.match(/^\/kho\/([\w-]+)$/))) return khoCheckView(m[1]);
    if (p !== "/kho") { if (foreign(p)) denied = p; history.replaceState(null, "", "/kho"); }
    return khoQueueView(me);
  }
  if (me.shell === "CTV" || me.shell === "DEALER") {
    if (p === "/tim-sim") return searchView();
    if (p === "/giu-so") return myHoldsView();
    if (p === "/luyen-tap") return practiceView(me);
    if ((m = p.match(/^\/giu-so\/([\w-]+)$/))) return holdPageView(m[1], me);
    if (p === "/chot") return dealsListView(me);
    if ((m = p.match(/^\/chot\/([\w-]+)$/))) return dealView(me, m[1]);
    if (p === "/loc-3-lop") return loc3View();
    if ((m = p.match(/^\/bai-dang\/([\w-]+)$/))) return postComposerView(m[1]);
    if (p === "/khach/moi") return leadFormView(me);
    if (p === "/khach/tra-loi") return replyView();
    if ((m = p.match(/^\/khach\/([\w-]+)$/))) return leadDetailView(m[1]);
    if (p === "/khach") return customersView(me);
    if (p === "/thu-nhap") return walletView(me);
    if (p === "/bat-dau") return onboardingIntroView();
    if (p === "/bat-dau/nho-ho-tro") return helpLearnView();
    if (p === "/noi-dung") return memberContentView();
    if (p === "/viec") return myCasesView(me);
    if ((m = p.match(/^\/viec\/([\w-]+)$/))) return caseMemberView(m[1]);
    if (p === "/cong-no" && me.shell === "DEALER") return dealerLedgerView();
    if (p !== "/hom-nay") { if (foreign(p) || p.startsWith("/kho") || p === "/cong-no") denied = p; history.replaceState(null, "", "/hom-nay"); }
    return todayView(me);
  }
  return h("main", { class: "auth" }, h("section", { class: "auth-card" }, h("h1", {}, "Tài khoản cũ"), h("p", {}, "Tài khoản này thuộc bản thử cũ và không dùng trong bản v1.0.")));
}

// P4 OBS-02: a member who opens a screen of another role is told why they landed on their own home (the server refuses those reads anyway)
let denied: string | null = null;
let pushSynced = false;
const foreign = (p: string) => p.startsWith("/owner") || p.startsWith("/ke-toan");
// P4 OBS-03: a visible "Đang tải…" bar when a screen takes longer than a moment (the old screen used to stay still with no sign)
const loadingBar = () => { const b = h("div", { class: "loading-bar", role: "status", "aria-live": "polite" }, "Đang tải…");
  b.style.cssText = "position:fixed;top:0;left:0;right:0;z-index:1000;padding:6px 12px;text-align:center;font-size:14px;font-weight:600;background:#0B1F3B;color:#fff"; return b; };
let shown = false;   // a screen of this session is on display (D-01: a network failure keeps it, with its header and navigation)
async function render(): Promise<void> {
  root.setAttribute("aria-busy", "true");
  let keep = false;
  const bar = loadingBar();
  const showBar = setTimeout(() => document.body.append(bar), 300);
  try {
    root.replaceChildren(await route()); shown = true;
    // consumed only once shown (a member's home may itself move on, e.g. a new CTV to /bat-dau, and the notice follows)
    if (denied) { (root.querySelector(".page, .ops-main") ?? root).prepend(banner("warn", h("p", {}, "Màn này dành cho vai khác; tài khoản của bạn không mở được. Bạn đang ở màn chính của mình."))); denied = null; }
  }
  catch (e) {
    keep = shown && isNetworkError(e);
    if (keep) {
      // the previous screen stays usable; a notice on top says the new screen did not open and offers a retry
      root.querySelector(".net-notice")?.remove();
      const retry = h("button", { class: "btn btn-secondary btn-sm", type: "button", onclick: () => void render() }, "Thử lại");
      const note = banner("error", h("p", {}, `Chưa mở được màn mới. ${NETWORK_MESSAGE}`), retry);
      note.classList.add("net-notice");
      (root.querySelector(".page, .ops-main") ?? root).prepend(note);
    } else root.replaceChildren(h("main", { class: "screen no-nav" }, h("div", { class: "page" }, errorBanner(e), h("button", { class: "btn btn-secondary", onclick: () => location.reload() }, "Tải lại"))));
  }
  finally { clearTimeout(showBar); bar.remove(); root.setAttribute("aria-busy", "false"); window.scrollTo(0, 0); }
}

export function startV1(): void {
  document.body.classList.add("v1");
  window.addEventListener("popstate", () => void render());
  window.addEventListener("b7b:navigate", () => void render());
  startBell();
  void render();
}
